import * as React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
    Alert, Box, Grid, Card, Typography, Button, Chip, TextField, InputAdornment,
    ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { AutocompleteAsyncDebounced, SiteSelect } from "@components"
import { BACKEND_APIS } from "@constants"
import BrandThumbnail, { withThumbnails } from "./BrandThumbnail"

const VB = BACKEND_APIS.VISIBILITY_BRANDS
const idOf = (v) => (v && typeof v === 'object' ? v.value : v)

// Brand texts one Normalize run processes. Mirrors NORMALIZE_BATCH in the backend
// (app/services/brand_normalization.py), which is what actually enforces it — this copy
// only exists so the UI can name the number instead of saying "some".
const NORMALIZE_BATCH = 100

// Rows the listing shows, and the only page there is — this tab does not paginate.
// Same number as the batch on purpose: the rows on screen are the rows the button acts
// on. Paging over a set the button mutates reshuffles it, so page 2 would skip rows.
const PAGE_SIZE = NORMALIZE_BATCH

/**
 * Visibility brand normalization (Puig).
 *
 * Raw brand text comes from ITEM_VISIBILITY_RAW (ClickHouse) and the canonical
 * id_brand is written to DIPI_BRAND_MAPPING — a dictionary keyed by the TEXT ALONE.
 * That makes every change global (all sites, all customers, whole history) and
 * retroactive, since the dashboards resolve the brand at query time. It is the point
 * of this tab, but it has to be obvious on screen.
 *
 * Saving stamps qa = 1, which keeps the automatic pipeline from overwriting the row.
 * It does NOT lock it against an operator: an already validated row can be reassigned
 * from here at any time.
 */
export default function VisibilityBrandsPanel({ cid, site: selectedSite, onSiteChange }) {
    const { showToast } = useAppToast()

    // The WHOLE site catalog, fetched once per customer: the selector filters it in
    // the browser instead of asking the backend on every keystroke.
    const [siteOptions, setSiteOptions] = useState([])
    const [loadingSites, setLoadingSites] = useState(false)
    // Canonical brand filter. The ID and not the option object: AutocompleteAsyncSeeker
    // echoes whatever it is given back to the backend as `selected`, which int()s it —
    // handing it the object answered 500 on every keystroke.
    const [brandId, setBrandId] = useState(null)
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('all')       // same default as dipi
    const [dateFrom, setDateFrom] = useState('')      // empty = no date filter, like dipi
    // Totals of the WHOLE filtered set, sent by the backend (window functions over the
    // ClickHouse query): summing `rows` would only describe the page on screen.
    const [total, setTotal] = useState(0)
    const [totalOccurrences, setTotalOccurrences] = useState(0)
    const [totalPending, setTotalPending] = useState(0)
    // The LIMIT cut the list — as opposed to the MySQL override dropping rows the
    // lagging mirror still counted as pending. Only the first justifies "showing the
    // first N of M"; the second made the screen claim 4 of 42 when 4 was all there was.
    const [truncated, setTruncated] = useState(false)
    // The Sync filter's candidate list was capped by the backend, so the answer is a
    // partial one. Only ever true under that filter.
    const [syncTruncated, setSyncTruncated] = useState(false)
    const [assignTarget, setAssignTarget] = useState(null)   // {brand}
    const [assignBrand, setAssignBrand] = useState(null)
    const [savingAssign, setSavingAssign] = useState(false)
    const [running, setRunning] = useState(false)     // keyword normalize in flight

    // The site is picked on the page and shared with the Product tab, but the two tabs read
    // DIFFERENT catalogs (348 visibility sites against 828 product ones), so the shared pick
    // may name a site this tab has no data for. Resolve it here and take the catalog's own
    // object: the label and the flag come from it. An unknown site leaves the box empty
    // WITHOUT clearing the shared value, so the other tab keeps its selection.
    const site = useMemo(
        () => (selectedSite ? siteOptions.find(o => o.value === selectedSite.value) || null : null),
        [selectedSite, siteOptions]
    )
    // 'all' is the synthetic option the backend puts on top of the site list. Note it
    // is a string, so `siteId` can never be tested for truthiness on its own.
    const siteId = idOf(site)
    const isAllSites = siteId === 'all'
    const hasSite = siteId !== null && siteId !== undefined
    // Only once the catalog is in: while it is on the wire nothing has resolved yet.
    const siteUnavailable = !!selectedSite && !site && !loadingSites && siteOptions.length > 0

    // The site list belongs to the customer, so a customer change invalidates it.
    useEffect(() => {
        setBrandId(null)
        if (!cid) { setSiteOptions([]); return }
        setLoadingSites(true)
        getSubmit(`${VB}/sites`, { customerID: cid, all: 1 })
            .then(res => setSiteOptions(res || []))
            .catch(() => { setSiteOptions([]); showToast.error('Could not load sites') })
            .finally(() => setLoadingSites(false))
    }, [cid])

    const load = useCallback(() => {
        if (!cid || !hasSite) { setRows([]); setTotal(0); setTotalOccurrences(0); setTotalPending(0); setTruncated(false); setSyncTruncated(false); return }
        setLoading(true)
        const params = new URLSearchParams({
            customerID: cid, limit: PAGE_SIZE,
        })
        // No siteID at all = every site of the customer.
        if (!isAllSites) params.set('siteID', siteId)
        if (search.trim()) params.set('search', search.trim())
        if (status !== 'all') params.set('status', status)
        if (brandId) params.set('brandID', brandId)
        if (dateFrom) params.set('dateFrom', dateFrom)
        getSubmit(`${VB}?${params.toString()}`)
            .then(res => {
                // Thumbnail URLs resolved once for the page, never per row.
                setRows(withThumbnails(res?.brands || [], 'thumbnail'))
                setTotal(res?.total || 0)
                setTotalOccurrences(res?.total_occurrences || 0)
                setTotalPending(res?.total_pending || 0)
                setTruncated(!!res?.truncated)
                setSyncTruncated(!!res?.sync_truncated)
            })
            .catch(() => {
                showToast.error('Could not load visibility brands')
                setRows([]); setTotal(0); setTotalOccurrences(0); setTotalPending(0); setTruncated(false); setSyncTruncated(false)
            })
            .finally(() => setLoading(false))
    }, [cid, siteId, hasSite, isAllSites, search, status, dateFrom, brandId])

    // Pending rows AMONG THE ONES ON SCREEN — the exact set Normalize will act on,
    // now that the listing returns one page of PAGE_SIZE and the run reads the same
    // filters. `normalized` is the MySQL-backed flag the listing already resolved.
    const pendingHere = useMemo(() => rows.filter(r => !r.normalized).length, [rows])

    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    const isSync = status === 'sync'

    // Per-page figure on purpose: the warning below says "row(s) HERE".
    const pendingSync = useMemo(() => rows.filter(r => r.pending_sync).length, [rows])

    // Whether the toolbar is narrowing what the table shows. Drives both the scope sent
    // to the backend and the button's own label: the run used to cover the whole site
    // regardless of the filters, which is worse here than in Product because every write
    // lands on every site.
    const isFiltered = !!(search.trim() || dateFrom)

    // One string, shown above AND below the table. Only when the LIMIT actually cut the
    // list — rows the MySQL override dropped are not a truncation (see `truncated`).
    const truncatedNotice = truncated && !loading
        ? `Showing ${rows.length} of ${total.toLocaleString()} brand texts`
          + ' — narrow the search to reach the rest.'
        : null

    const runNormalize = async () => {
        // All sites is allowed now. It was blocked because an uncapped run there meant the
        // customer's 183k pending texts in one unsupervised write; with the batch cap the
        // run is the 100 rows on screen, and every write was global regardless of which
        // site they were read from.
        if (!cid || !hasSite) return
        setRunning(true)
        try {
            // Only sent when set: an absent filter means the whole site, the original
            // behaviour.
            // No id_site at all on All sites, the same shape the listing sends.
            const body = { id_customer: cid }
            if (!isAllSites) body.id_site = siteId
            if (search.trim()) body.search = search.trim()
            if (dateFrom) body.dateFrom = dateFrom
            if (status !== 'all') body.status = status
            const res = await postSubmit(`${VB}/normalize`, body)
            // The run is capped, so saying "Normalized 87 of 100" alone would read as
            // "done" with 19.800 still queued. The remainder is the headline.
            // `capped` means the BATCH cut this run, so there is a real queue behind it.
            // Anything else drained what was on screen and needs no epilogue.
            const left = Math.max((res.pending_total || 0) - (res.matched_texts || 0), 0)
            const wrote = res.matched_texts || 0
            // Green says "done"; a run that resolved nothing is a report, not an
            // achievement. Same text, honest colour.
            showToast[wrote ? 'success' : 'info'](
                `Normalized ${res.matched_texts} of ${res.pending_texts} brands`
                + ` · ambiguous ${res.ambiguous_texts} · unmatched ${res.unmatched_texts}`
                + (res.capped ? ` · ${left.toLocaleString()} still pending` : ''))
            // Only when something changed. The reload is the ClickHouse query again —
            // 2,2 s on a site, a 353M-row scan on All sites without filters — and with
            // nothing written the table on screen is already correct.
            if (wrote) load()
        } catch (e) {
            showToast.error('Error normalizing visibility brands')
        } finally {
            setRunning(false)
        }
    }

    // Confirmed, and the only one of the four that is both BULK and GLOBAL. Short like
    // the rest, but the global reach stays in: it is the consequence, not a detail. What
    // the matcher does and what it skips lives in the button's tooltip.
    //
    // The batch cap is what makes this figure honest at last: the run does the first
    // NORMALIZE_BATCH pending texts, so the number is known up front and no longer has to
    // be guessed from `total` (which, with the status toggle on All, is every text of the
    // site — 74.830 on site 360, four times what would be written).
    const confirmNormalize = () => {
        if (!pendingHere) {
            showToast.info(total
                ? 'Nothing pending among the rows shown — narrow the search to reach the rest.'
                : 'No brand texts match this filter — nothing to normalize.')
            return
        }
        // JSX, not a string: ConfirmContent renders `message` inside a Typography, so the
        // scope can carry the emphasis. "shown" is the word that bounds the whole action.
        showToast.confirm(
            <>
                Normalize the {pendingHere} pending text{pendingHere === 1 ? '' : 's'}
                {' '}<strong>shown</strong>? Applies to every site and customer.
                {' '}Cannot be undone.
            </>,
            { onSubmit: runNormalize })
    }

    // `brandArg` lets Clear act straight from a row without opening the dialog: reading
    // assignTarget right after setAssignTarget would still see the previous value (the
    // state update lands on the next render), and it is null on a fresh page.
    const doAssign = async (id_brand, brandArg) => {
        const brand = brandArg ?? assignTarget?.brand
        if (!brand) return
        setSavingAssign(true)
        try {
            await postSubmit(`${VB}/assign`, { brand, id_brand })
            showToast.success(id_brand
                ? 'Brand mapped for every site — dashboards refresh in a few minutes'
                : 'Mapping cleared for every site — the normalizer will try again')
            setAssignTarget(null); setAssignBrand(null)
            load()
        } catch (e) {
            showToast.error(id_brand ? 'Error mapping brand' : 'Error clearing the mapping')
        } finally {
            setSavingAssign(false)
        }
    }

    return (
        <Grid container spacing={2}>
            {/* Toolbar */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* The filters wrap among THEMSELVES. Keeping them in their own box
                        is what stops the counts and the Normalize button from being
                        pushed onto a line of their own once the brand filter joined the
                        row — they belong top right, where they were. */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center',
                               flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    <Box sx={{ minWidth: 260 }}>
                        <SiteSelect
                            options={siteOptions}
                            value={site}
                            onChange={onSiteChange}
                            loading={loadingSites}
                            allOption
                        />
                    </Box>
                    {/* Only a resolved row can carry a canonical brand, so picking one
                        moves the toggle to Normalized. It does NOT disable the others:
                        "this brand + Not synced" — assigned here, not in the dashboards
                        yet — is a question worth asking (decision 2026-09-10). */}
                    <Box sx={{ minWidth: 240 }}>
                        <AutocompleteAsyncDebounced
                            urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                            label="Canonical brand"
                            placeholder="All brands"
                            queryParam="search"
                            value={brandId}
                            onChange={(e, op) => {
                                setBrandId(e.target.value || null)
                                if (op?.value) setStatus('normalized')
                            }}
                        />
                    </Box>
                    <TextField size="small" placeholder="Search raw brand…" value={search}
                               onChange={e => setSearch(e.target.value)} sx={{ minWidth: 280 }}
                               InputProps={{ startAdornment: (
                                   <InputAdornment position="start"><IconSeeker icon="search" sx={{ color: '#9ca3af' }} /></InputAdornment>
                               ) }} />
                    <ToggleButtonGroup size="small" exclusive value={status}
                                       onChange={(e, v) => v && setStatus(v)}>
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="normalized">Normalized</ToggleButton>
                        {/* Native title, never a MUI Tooltip: one blanked this route's
                            dev build already (VersionBadge), and the hint is one line. */}
                        {/* The value stays 'sync' — that is the API's status — while the
                            label says the state rather than the process: "Pending Sync"
                            next to "Pending" reads as two kinds of pending. */}
                        <ToggleButton value="sync"
                                      title="Assigned here, not in the dashboards yet — the rows with the sync icon">
                            Not synced
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" type="date" label="Seen since"
                               value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                               InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }}
                               inputProps={{ title: 'Only rows seen on/after this date. Empty = whole history' }} />
                    </Box>
                    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                    {/* No Tooltip around this Button: it toggles between disabled and
                        enabled with the site, and MUI's Tooltip renders a disabled child
                        differently — that swap desyncs the hook order ("Should have a
                        queue"). The hint lives in the button's own title. */}
                    {/* The label carries the CAP: this button does a batch, never
                        "everything". Uncapped it was ~24 minutes on this very site and died
                        on a gateway timeout with the write half done. */}
                    <Button variant="contained" disabled={!hasSite || running}
                            title={'Map the pending brand texts listed here that the keyword matcher'
                                + ' resolves unambiguously. Each mapping applies globally, like any'
                                + ' mapping here.'}
                            startIcon={running ? <CircularProgress size={16} /> : <IconSeeker icon="wand-magic-sparkles" />}
                            onClick={confirmNormalize}>
                        Normalize (keywords)
                    </Button>
                    </Box>
                </Box>
                {/* Its own line under the toolbar, right-aligned. Inside the row —
                    beside the button or stacked under it — this text is ~340px wide and
                    the flex column inherits that width, which is what kept pushing Seen
                    since onto a line of its own. */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                    {/* Unlike the Product tab, whose state is read straight from MySQL,
                        these counts come from the ClickHouse mirror of BRAND_MAPPING
                        whenever the page was cut by the limit — the status filter has to run
                        inside ClickHouse, before the LIMIT, or the pending texts (which live
                        in the long tail of a 40k-text site) would never make it onto the
                        page. The mirror lags by exactly the work just done, which is why the
                        caveat below is worth the line: right after resolving 38 texts this
                        read "1085 brands · 42 pending" while the Pending filter, counted by
                        MySQL, showed 4. Unclipped pages are counted by MySQL and exact, so
                        the caveat only shows when it applies. */}
                    {hasSite && !loading && (
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                                {total.toLocaleString()} brands
                                {/* Under Sync every row IS assigned, so the pending count
                                    describes the opposite population and reads as
                                    nonsense next to it. */}
                                {isSync
                                    ? <>{' · '}{total.toLocaleString()} waiting for refresh</>
                                    : <>{' · '}{totalPending.toLocaleString()} pending</>}
                                {' · '}{totalOccurrences.toLocaleString()} occurrences
                            </Typography>
                            {syncTruncated && (
                                <Typography variant="caption" color="text.disabled"
                                            sx={{ display: 'block', lineHeight: 1.3 }}>
                                    Newest writes only — a bulk normalize can leave more
                                    waiting than this list holds.
                                </Typography>
                            )}
                            {truncated && (
                                <Typography variant="caption" color="text.disabled"
                                            sx={{ display: 'block', lineHeight: 1.3 }}>
                                    Counted from ClickHouse — may lag a few minutes behind
                                    what you just changed.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Grid>

            {hasSite && (
                <Grid item xs={12}>
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                        Mapping a brand here applies to <strong>every site and customer</strong> and
                        to the whole history — the dashboards resolve the brand at query time, and
                        the automatic normalizer will not overwrite it afterwards. Dashboards pick
                        the change up a few minutes later.
                        {isAllSites && (
                            <> · Listing <strong>every site</strong> of this customer over the whole
                            history; set <em>Seen since</em> to make it faster.</>
                        )}
                        {pendingSync > 0 && (
                            <> · {pendingSync} row(s) here are still waiting for that refresh.</>
                        )}
                    </Alert>
                </Grid>
            )}

            {/* Same line as the one under the table. Repeated on purpose: the
                table is 100 rows tall, so a note only at the bottom is a note
                nobody reads before acting on what they can see. */}
            {truncatedNotice && (
                <Grid item xs={12} sx={{ pb: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                        {truncatedNotice}
                    </Typography>
                </Grid>
            )}

            {/* Table */}
            <Grid item xs={12}>
                {!hasSite ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <IconSeeker icon="camera-viewfinder" sx={{ fontSize: 48, mb: 1 }} />
                        {/* The site is shared with Product, whose catalog is not this one.
                            Saying so beats an empty box that looks like the filter was lost. */}
                        <Typography>
                            {siteUnavailable
                                ? `"${selectedSite.label}" has no visibility data for this customer — pick another site.`
                                : 'Select a site — or All sites — to review the brands its visibility data reports.'}
                        </Typography>
                    </Box>
                ) : (
                    <Card variant="outlined">
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                        ) : rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No brands found.</Box>
                        ) : (
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 96 }} />
                                        <TableCell>Raw brand</TableCell>
                                        {/* Occurrences, not products: the value is count()
                                            over ITEM_VISIBILITY_RAW, i.e. how many times the
                                            text was SEEN in a widget — CELINE reads 109.814
                                            on one site. "Products" was copied from the
                                            Product tab, where the column really is a count
                                            of distinct product_ids. The header counter to the
                                            right of the toolbar already said "occurrences";
                                            these two are the same number. */}
                                        <TableCell align="right"
                                                   title="Times this text was seen in a visibility widget, not distinct products">
                                            Occurrences
                                        </TableCell>
                                        <TableCell>Last seen</TableCell>
                                        <TableCell>Canonical brand</TableCell>
                                        <TableCell align="center"
                                                   title="Validated by a QA (qa = 1): the automatic normalizer will not overwrite this text">
                                            QA
                                        </TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(r => (
                                        <TableRow key={r.brand} hover>
                                            {/* The most recently seen occurrence of this text. Worth
                                                the column here above all: on the 26 sites that scrape
                                                an empty brand the text IS a product title, often
                                                transliterated Korean, and the picture says in a glance
                                                what the text makes you decipher. */}
                                            <TableCell sx={{ width: 96, py: 0.5 }}>
                                                <BrandThumbnail src={r.thumbnail_url} alt={r.brand} />
                                            </TableCell>
                                            {/* Native `title` instead of MUI's Tooltip: this table
                                                renders up to 1.000 rows, and one Tooltip per row (each
                                                a component with its own hooks) is what made the panel
                                                blow up on the big sites. */}
                                            <TableCell title={r.brand}
                                                       sx={{ fontWeight: 500, maxWidth: 420,
                                                             overflow: 'hidden', textOverflow: 'ellipsis',
                                                             whiteSpace: 'nowrap' }}>
                                                {r.brand}
                                            </TableCell>
                                            <TableCell align="right">{r.occurrences.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {r.last_seen || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    {r.normalized ? (
                                                        <Chip size="small" variant="outlined" color="success"
                                                              label={r.canonical_name || r.id_brand} />
                                                    ) : (
                                                        <Chip size="small" variant="outlined" label="— pending"
                                                              sx={{ color: 'text.disabled' }} />
                                                    )}
                                                    {r.pending_sync && (
                                                        <IconSeeker icon="arrows-rotate" sx={{ fontSize: 13, color: 'warning.main' }}
                                                                    title="Saved — the dashboards still read the previous value until the mirror refreshes" />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            {/* Read-only, and the reason it exists: 4.019 texts
                                                carry qa = 1 with NO brand — a QA looked at them
                                                and decided they will never be one (a book title,
                                                a Korean ad headline). They keep showing up as
                                                pending, so this chip is the only thing telling
                                                them apart from a text nobody has reviewed. */}
                                            <TableCell align="center"
                                                       title={r.qa
                                                           ? 'A QA validated this text; the automatic normalizer will not overwrite it'
                                                           : 'Not reviewed by a QA'}>
                                                {r.qa ? (
                                                    <Chip size="small" variant="outlined" color="success" label="QA" />
                                                ) : (
                                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button size="small"
                                                        onClick={() => { setAssignTarget({ brand: r.brand }); setAssignBrand(null) }}>
                                                    {r.normalized ? 'Reassign' : 'Assign'}
                                                </Button>
                                                {/* Confirmed, and worded harder than Product's: the
                                                    key of DIPI_BRAND_MAPPING is the text alone, so
                                                    this does not undo one site — it drops the mapping
                                                    for every site, every customer and the whole
                                                    history, with no undo. */}
                                                {r.normalized && (
                                                    <Button size="small" color="error"
                                                            title="Removes the mapping everywhere (all sites and customers) and hands the text back to the normalizer"
                                                            onClick={() => showToast.confirm(
                                                                <>Clear "{r.brand}" on <strong>every site and
                                                                customer</strong>? Cannot be undone.</>,
                                                                { onSubmit: () => doAssign(null, r.brand) })}>
                                                        Clear
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {/* No pager. The list is one page of PAGE_SIZE and that page is
                            exactly what Normalize acts on; paging over a set the button
                            mutates reshuffles it, so page 2 would skip rows. The header
                            carries the real total, so the tail is stated, not hidden. */}
                        {truncatedNotice && (
                            <Box sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                                <Typography variant="body2">{truncatedNotice}</Typography>
                            </Box>
                        )}
                    </Card>
                )}
            </Grid>

            <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>Map visibility brand</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Raw brand: <strong>{assignTarget?.brand}</strong>
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                        Applies to every site and customer, past data included.
                    </Alert>
                    <AutocompleteAsyncDebounced
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Canonical brand (DIPI_BRAND_NEW)"
                        placeholder="Search canonical brand…"
                        value={assignBrand}
                        onChange={e => setAssignBrand(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignTarget(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!idOf(assignBrand) || savingAssign}
                            startIcon={savingAssign ? <CircularProgress size={16} /> : null}
                            onClick={() => doAssign(idOf(assignBrand))}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
